import axios from 'axios';
import { get } from 'lodash';
import confirm from 'react-confirm2';

export const START_SCREENING = 'app/healix/START_SCREENING';
export const FIND_CONDITION = 'app/healix/FIND_CONDITION';
export const START_DECLARATION = 'app/healix/START_DECLARATION';
export const DELETE_DECLARATION = 'app/healix/DELETE_DECLARATION';
export const SET_ANSWER = 'app/healix/SET_ANSWER';
export const FINISH_DECLARATION = 'app/healix/FINISH_DECLARATION';
export const FINISH_SCREENING = 'app/healix/FINISH_SCREENING';
export const IS_SUBMITTING = 'app/healix/IS_SUBMITTING';
export const IS_SEARCHING = 'app/healix/IS_SEARCHING';
export const IS_ABOUT_TO_SCREEN = 'app/healix/IS_ABOUT_TO_SCREEN';
export const IS_EDITING = 'app/healix/IS_EDITING';
export const IS_DELETING = 'app/healix/IS_DELETING';
export const SET_INDEX = 'app/healix/SET_INDEX';
export const IS_RECALCULATING_SCORE = 'app/healix/IS_RECALCULATING_SCORE';

const initialState = {
  isSubmitting: false,
  isSearching: false,
  isEditing: false,
  isDeleting: false,
  isRecalculatingScore: 0,
  isAboutToScreen: false,
  index: 0,
  token: '',
  complete: false,
  conditions: [],
  declaredConditions: {},
  declaration: {},
  result: {},
  hasMisspelledConditions: false,
};

export function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_INDEX:
      return {
        ...initialState,
        index: action.index,
      };
    case IS_SUBMITTING:
      return {
        ...state,
        isSubmitting: action.status,
      };
    case IS_RECALCULATING_SCORE:
      return {
        ...state,
        isRecalculatingScore: action.status ? state.isRecalculatingScore++ : state.isRecalculatingScore--,
      };
    case IS_SEARCHING:
      return {
        ...state,
        isSearching: action.status,
      };
    case IS_EDITING:
      return {
        ...state,
        isEditing: action.status,
      };
    case IS_DELETING:
      return {
        ...state,
        isDeleting: action.status,
      };
    case START_SCREENING:
      return {
        ...state,
        complete: !!action.conditions,
        token: action.token,
        declaredConditions: action.conditions,
      };
    case FIND_CONDITION:
      return {
        ...state,
        conditions: action.data,
        hasMisspelledConditions: action.hasMisspelledConditions,
      };
    case START_DECLARATION:
      return {
        ...state,
        complete: false,
        declaration: action.declaration,
      };
    case SET_ANSWER:
      return {
        ...state,
        declaration: action.declaration,
      };
    case FINISH_DECLARATION:
      return {
        ...state,
        complete: true,
        declaration: {},
        declaredConditions: action.conditions,
      };
    case DELETE_DECLARATION:
      return {
        ...state,
        declaredConditions: action.conditions,
      };
    case FINISH_SCREENING:
      return {
        ...state,
        index: 0,
        result: action.data,
      };
    default:
      return state;
  }
}

export function setIndex(index) {
  return {
    type: SET_INDEX,
    index,
  };
}

export function isSubmitting(status) {
  return {
    type: IS_SUBMITTING,
    status,
  };
}

export function isRecalculatingScore(status) {
  return {
    type: IS_RECALCULATING_SCORE,
    status,
  };
}

export function isEditing(status) {
  return {
    type: IS_EDITING,
    status,
  };
}

export function isDeleting(status) {
  return {
    type: IS_DELETING,
    status,
  };
}

export function isSearching(status) {
  return {
    type: IS_SEARCHING,
    status,
  };
}

export function isAboutToScreen(status) {
  return {
    type: IS_ABOUT_TO_SCREEN,
    isAboutToScreen: true,
  };
}

function startHealixSession(region, destinations, id) {
  return axios.post(
    '/public/medical/healix:start',
    {
      meta: {
        region,
        destinations,
        id,
      },
    },
    { headers: { 'Content-Type': 'application/vnd.api+json' } },
  );
}

function finishHealixSession(id) {
  return axios.post(
    '/public/medical/healix:finish',
    {
      meta: {
        id,
      },
    },
    { headers: { 'Content-Type': 'application/vnd.api+json' } },
  );
}

const INCLUDED = '?include=conditions,conditions.questions.options,conditions.questions.selected';

export function startScreening(travellerIndex, screeningId, region, destinations = [], callback) {
  return (dispatch) => {
    dispatch(setIndex(travellerIndex));
    dispatch(isSubmitting(true));

    return startHealixSession(region, destinations, screeningId)
      .then((sessionResponse) => {
        if (screeningId) {
          return axios
            .get('/public/medical/healix/' + sessionResponse.data.meta.id + INCLUDED)
            .then((res) => {
              dispatch({
                type: START_SCREENING,
                token: sessionResponse.data.meta.id,
                conditions: res.data,
              });
              dispatch(isSubmitting(false));

              callback(sessionResponse.data.meta.id);

              return true;
            })
            .catch((err) => {
              dispatch(isSubmitting(false));
              console.error(err);
            });
        }

        dispatch({
          type: START_SCREENING,
          token: sessionResponse.data.meta.id,
          conditions: {},
        });
        dispatch(isSubmitting(false));

        callback(sessionResponse.data.meta.id);

        return true;
      })
      .catch((err) => {
        dispatch(isSubmitting(false));
        console.error(err);
      });
  };
}

export function recalculateScore(screeningId, region, destinations = [], callback) {
  return (dispatch, getState) => {
    dispatch(isRecalculatingScore(true));

    return axios.post(
      '/public/medical/healix:recalculate', {
        meta: {
          region,
          destinations,
          id: screeningId,
        },
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    ).then((res) => {
      if (callback) {
        callback(
          res.data.meta.conditions.length ? res.data : '', // screening payload
          res.data.meta.conditions.length ? screeningId : '', // screening id
        );
      }
      dispatch(isRecalculatingScore(false));
    }).catch((err) => {
      dispatch(isRecalculatingScore(false));
      console.error(err);
    });
  };
}

export function finishScreening(callback) {
  return (dispatch, getState) => {
    dispatch(isSubmitting(true));
    const state = getState().healix;

    return finishHealixSession(state.token).then((res) => {
      dispatch({
        type: FINISH_SCREENING,
        data: res.data,
      });

      callback(
        res.data.meta.conditions.length ? res.data : '',
        res.data.meta.conditions.length ? state.token : '',
      );

      dispatch(isSubmitting(false));
      return true;
    }).catch((err) => {
      dispatch(isSubmitting(false));
      console.error(err);
    });
  };
}

export function cancelDeclaration() {
  return (dispatch, getState) => {
    const state = getState().healix;
    dispatch(isSubmitting(true));

    return axios.delete('/public/medical/healix/' + state.token + '/declaration', {
      finish: true,
    }).then((res) => {
      return axios.get('/public/medical/healix/' + state.token).then((res) => {
        dispatch({
          type: FINISH_DECLARATION,
          conditions: res.data,
        });
        dispatch(isSubmitting(false));
        return true;
      }).catch((err) => {
        dispatch(isSubmitting(false));
        console.error(err);
      });
    }).catch((err) => {
      dispatch(isSubmitting(false));
      console.error(err);
    });
  };
}

export function findCondition(search) {
  return (dispatch, getState) => {
    const state = getState().healix;

    if (!search.length || search.length < 2) {
      return null;
    }

    dispatch(isSearching(true));

    axios
      .get('/public/medical/healix/' + state.token + '/conditions?filter[name]=' + encodeURIComponent(search))
      .then((res) => {
        const options = res.data.data.attributes.conditions.map((condition) => {
          return {
            value: condition.id,
            label: condition.name,
          };
        }).filter(Boolean);

        dispatch(isSearching(false));

        dispatch({
          type: FIND_CONDITION,
          data: options,
          hasMisspelledConditions: res.data.data.attributes.has_misspelled_conditions,
        });
        return true;
      }).catch((err) => {
        dispatch(isSearching(false));
        console.error(err);
      });
  };
}

export function startDeclaration(condition) {
  return (dispatch, getState) => {
    const state = getState().healix;
    dispatch(isSubmitting(true));

    return axios.post(
      '/public/medical/healix/' + state.token + '/declaration' + INCLUDED,
      {
        id: condition.value,
        name: condition.label,
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    ).then((res) => {
      dispatch({
        type: START_DECLARATION,
        index: 0,
        declaration: res.data,
      });

      dispatch(isSubmitting(false));
      return true;
    }).catch((err) => {
      const message = get(err.response, 'data.errors[0].detail');
      confirm(message, {
        done: () => {
          console.log(message);
        },
      });
      dispatch(isSubmitting(false));
      console.error(err);
      throw err;
    });
  };
}

export function editDeclaration(declarationId) {
  return (dispatch, getState) => {
    const state = getState().healix;
    dispatch(isEditing(declarationId));

    return axios.post(
      '/public/medical/healix/' + state.token + '/declaration' + INCLUDED,
      {
        id: declarationId,
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    ).then((res) => {
      dispatch({
        type: START_DECLARATION,
        index: 0,
        declaration: res.data,
      });

      dispatch(isEditing(false));
      return true;
    }).catch((err) => {
      dispatch(isEditing(false));
      console.error(err);
    });
  };
}

export function finishDeclaration() {
  return (dispatch, getState) => {
    const state = getState().healix;
    dispatch(isSubmitting(true));

    return axios.patch(
      '/public/medical/healix/' + state.token + '/declaration' + INCLUDED,
      {
        finish: true,
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    ).then((res) => {
      return axios.get('/public/medical/healix/' + state.token + INCLUDED).then((res) => {
        dispatch({
          type: FINISH_DECLARATION,
          conditions: res.data,
        });
        dispatch(isSubmitting(false));
        return true;
      }).catch((err) => {
        dispatch(isSubmitting(false));
        console.error(err);
      });
    }).catch((err) => {
      dispatch(isSubmitting(false));
      console.error(err);
    });
  };
}

export function deleteDeclaration(declarationId, name) {
  return (dispatch, getState) => {
    const state = getState().healix;
    dispatch(isDeleting(declarationId));

    return axios
      .delete(
        '/public/medical/healix/' + state.token + '/conditions/' + declarationId,
        { headers: { 'Content-Type': 'application/vnd.api+json' } },
      )
      .then(() => {
        axios
          .get('/public/medical/healix/' + state.token)
          .then((res) => {
            dispatch({
              type: DELETE_DECLARATION,
              conditions: res.data,
            });
            dispatch(isDeleting(false));

            // Resolve
            return res;
          })
          .catch((err) => {
            dispatch(isDeleting(false));
            console.error(err);

            // Reject
            throw err;
          });
      })
      .catch((err) => {
        dispatch(isDeleting(false));
        console.error(err);

        throw err;
      });
  };
}

export function setAnswer(condition, question, answer, sendAnswerAsObject = false) {
  return (dispatch, getState) => {
    const state = getState().healix;

    dispatch(isSubmitting(true));

    return axios.patch(
      '/public/medical/healix/' + state.token + '/declaration/conditions/' + condition.attributes.number + '/questions/'
      + question.attributes.number + INCLUDED,
      sendAnswerAsObject ? answer : {
        id: answer.value,
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    ).then((res) => {
      dispatch({
        type: SET_ANSWER,
        declaration: res.data,
      });

      dispatch(isSubmitting(false));
      return true;
    }).catch((err) => {
      dispatch(isSubmitting(false));
      console.error(err);
    });
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/services/healix/reducer.ts